import { escapeCsvCell } from '@/lib/security/input';

/**
 * A column is either a row key or a key with its own header and reader.
 * The header row is the column list, in order.
 */
export type CsvColumn<T> = string | { key: string; header?: string; value?: (row: T) => unknown };

function columnKey<T>(c: CsvColumn<T>): string {
  return typeof c === 'string' ? c : c.key;
}
function columnHeader<T>(c: CsvColumn<T>): string {
  return typeof c === 'string' ? c : (c.header ?? c.key);
}
function columnValue<T>(c: CsvColumn<T>, row: T): unknown {
  if (typeof c !== 'string' && c.value) return c.value(row);
  return (row as Record<string, unknown>)[columnKey(c)];
}

/**
 * A finite number is written as it is. Everything else goes through the
 * formula-injection guard, which also handles quoting, commas and newlines
 * and renders null and undefined as an empty cell. The guard prefixes a
 * leading `-` or `+`, which is right for text and wrong for `-3`.
 */
function cell(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return escapeCsvCell(value);
}

/** UTF-8 text, comma separated, CRLF line ends, header row first, no BOM. */
export function toCsv<T>(rows: readonly T[], columns: readonly CsvColumn<T>[]): string {
  const lines = [columns.map((c) => cell(columnHeader(c))).join(',')];
  for (const row of rows) lines.push(columns.map((c) => cell(columnValue(c, row))).join(','));
  return lines.join('\r\n') + '\r\n';
}

/** `appstoretracker-<surface>-<qualifiers>-<YYYY-MM-DD>.csv`, lowercase, joined by `-`. */
export function csvFileName(
  surface: string,
  qualifiers: readonly (string | number | null | undefined)[] = [],
  date: Date = new Date(),
): string {
  const parts = ['appstoretracker', surface, ...qualifiers, date.toISOString().slice(0, 10)]
    .filter((p) => p !== null && p !== undefined && String(p).trim() !== '')
    .map((p) =>
      String(p)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, ''),
    )
    .filter(Boolean);
  return `${parts.join('-')}.csv`;
}
